import { z } from 'zod';
/* =========================================================================
 * Roleplay granular WS contract (Feature B-01)
 *
 * Outbound commands (client -> server):
 *   sheet:rp-field-update
 *   sheet:rp-list-add
 *   sheet:rp-list-update
 *   sheet:rp-list-remove
 *   sheet:rp-board-config-update
 *
 * Broadcast events (server -> owner + GM):
 *   sheet:rp-field-updated
 *   sheet:rp-list-changed     (discriminated by `op`: 'add' | 'update' | 'remove')
 *   sheet:rp-board-config-updated
 *
 * Suppression of these events for viewers with viewOtherSheets==='basic'
 * is handled server-side by filterSheetForRole in the broadcast pipeline
 * (see server/src/commands/sheet-rp.ts). The shapes below are wire-only.
 * ========================================================================= */
/* ----- Message-type constants (single source of truth for topic strings) - */
export const SHEET_RP_MSG = {
    // outbound (commands)
    fieldUpdate: 'sheet:rp-field-update',
    listAdd: 'sheet:rp-list-add',
    listUpdate: 'sheet:rp-list-update',
    listRemove: 'sheet:rp-list-remove',
    boardConfigUpdate: 'sheet:rp-board-config-update',
    // inbound (broadcast events)
    fieldUpdated: 'sheet:rp-field-updated',
    listChanged: 'sheet:rp-list-changed',
    boardConfigUpdated: 'sheet:rp-board-config-updated',
};
/* ----- Shared enums ----------------------------------------------------- */
export const SHEET_RP_FIELDS = [
    'ideal',
    'bond',
    'flaw',
    'backstory',
    'manners',
];
export const SheetRpFieldSchema = z.enum(SHEET_RP_FIELDS);
export const SHEET_RP_LIST_KEYS = [
    'goals',
    'npcs',
    'factions',
    'sessionLog',
    'moments',
];
export const SheetRpListKeySchema = z.enum(SHEET_RP_LIST_KEYS);
export const SHEET_RP_LIST_OPS = ['add', 'update', 'remove'];
export const SheetRpListOpSchema = z.enum(SHEET_RP_LIST_OPS);
/* ----- Size limits (OWASP A04 — bound untrusted payloads) -------------- */
// Per-field cap for RP free text (ideal/bond/flaw/backstory/manners). 20k
// characters leaves room for long backstories without enabling DoS via
// multi-MB writes. Validated at the wire boundary so the server never
// allocates beyond this.
export const RP_FIELD_VALUE_MAX_CHARS = 20_000;
// Compact id strings — sheetId is a UUID (~36 chars) or short slug; list
// item ids generated server-side are ~25 chars. 128 is comfortably above
// any legitimate value.
export const RP_ID_MAX_CHARS = 128;
// Opaque record payload caps (measured by JSON-serialised byte length).
// `item` holds one card/goal/npc/faction entry; `changes` is a partial of
// the same shape; `config` carries the full board (cards + layout meta).
export const RP_LIST_ITEM_MAX_BYTES = 8_192;
export const RP_LIST_CHANGES_MAX_BYTES = 4_096;
export const RP_BOARD_CONFIG_MAX_BYTES = 65_536;
/** Refinement predicate: serialised JSON size of `val` ≤ `maxBytes`. */
function withinJsonSize(maxBytes) {
    return (val) => {
        try {
            return JSON.stringify(val).length <= maxBytes;
        }
        catch {
            // Cyclic / non-serialisable — reject; the wire requires JSON anyway.
            return false;
        }
    };
}
/* ----- HudBoardConfig (client-owned shape, transported as opaque object) - */
// The authoritative HudBoardConfig type lives in the client HUD store
// (src/lib/character-hud-store.ts). At the wire boundary we accept any
// JSON-serialisable object so the client can evolve internal structure
// without breaking the contract package — but capped at
// RP_BOARD_CONFIG_MAX_BYTES to prevent multi-MB writes.
export const HudBoardConfigSchema = z
    .record(z.string(), z.unknown())
    .refine(withinJsonSize(RP_BOARD_CONFIG_MAX_BYTES), {
    message: `boardConfig exceeds ${RP_BOARD_CONFIG_MAX_BYTES} bytes`,
});
/* ----- Generic helpers -------------------------------------------------- */
export const SheetRpListItemSchema = z
    .record(z.string(), z.unknown())
    .refine(withinJsonSize(RP_LIST_ITEM_MAX_BYTES), {
    message: `list item exceeds ${RP_LIST_ITEM_MAX_BYTES} bytes`,
});
export const SheetRpListChangesSchema = z
    .record(z.string(), z.unknown())
    .refine(withinJsonSize(RP_LIST_CHANGES_MAX_BYTES), {
    message: `list changes exceed ${RP_LIST_CHANGES_MAX_BYTES} bytes`,
});
/* ----- Outbound command schemas ---------------------------------------- */
const RpIdString = z.string().min(1).max(RP_ID_MAX_CHARS);
export const SheetRpFieldUpdateCommandSchema = z.object({
    sheetId: RpIdString,
    field: SheetRpFieldSchema,
    value: z.string().max(RP_FIELD_VALUE_MAX_CHARS),
});
export const SheetRpListAddCommandSchema = z.object({
    sheetId: RpIdString,
    listKey: SheetRpListKeySchema,
    item: SheetRpListItemSchema,
});
export const SheetRpListUpdateCommandSchema = z.object({
    sheetId: RpIdString,
    listKey: SheetRpListKeySchema,
    itemId: RpIdString,
    changes: SheetRpListChangesSchema,
});
export const SheetRpListRemoveCommandSchema = z.object({
    sheetId: RpIdString,
    listKey: SheetRpListKeySchema,
    itemId: RpIdString,
});
export const SheetRpBoardConfigUpdateCommandSchema = z.object({
    sheetId: RpIdString,
    config: HudBoardConfigSchema,
});
/* ----- Broadcast event schemas ----------------------------------------- */
export const SheetRpFieldUpdatedEventSchema = z.object({
    sheetId: z.string(),
    field: SheetRpFieldSchema,
    value: z.string(),
});
// sheet:rp-list-changed is a discriminated union on `op`.
// 'add'    -> carries full new item
// 'update' -> carries itemId + partial changes
// 'remove' -> carries itemId only
export const SheetRpListChangedEventSchema = z.discriminatedUnion('op', [
    z.object({
        sheetId: z.string(),
        listKey: SheetRpListKeySchema,
        op: z.literal('add'),
        item: SheetRpListItemSchema,
    }),
    z.object({
        sheetId: z.string(),
        listKey: SheetRpListKeySchema,
        op: z.literal('update'),
        itemId: z.string(),
        changes: SheetRpListChangesSchema,
    }),
    z.object({
        sheetId: z.string(),
        listKey: SheetRpListKeySchema,
        op: z.literal('remove'),
        itemId: z.string(),
    }),
]);
export const SheetRpBoardConfigUpdatedEventSchema = z.object({
    sheetId: z.string(),
    config: HudBoardConfigSchema,
});
